import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Cpu, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import WorkloadRiskModule from "@/components/WorkloadRiskModule";
import { ENGINES, SPORTS } from "@/lib/biohackerBrand";

/**
 * BioHacker Baseball™ — the flagship Sport Intelligence Package,
 * powered by the founder-favourite engine, ArmIQ™.
 *
 * Replaces the old baseball-first "Why ArmIQ?" hero — ArmIQ remains a
 * flagship engine, no longer the corporate identity.
 */

const AUDIENCES = [
  { id: "coaches",   label: "Coaches",             body: "Practice planning, individualized session loads, live scrubber studio, kinetic-sequence video review, tour-day comms." },
  { id: "trainers",  label: "Athletic Trainers",    body: "Daily wellness, injury surveillance, workload alerts, cumulative arm-stress, RTP progression, medical handshake." },
  { id: "sc",        label: "Strength Coaches",     body: "Force plates, VBT, jump analysis, adaptation tracking mapped to throwing load and recovery state." },
  { id: "director",  label: "Performance Directors", body: "Cross-team KPIs, testing management, technology integration audits, longitudinal development trees." },
  { id: "athletes",  label: "Athletes",             body: "The AthleteIQ™ app — daily plan, recovery routine, MotionIQ™ breakdowns, recruiting-grade profile." },
];

const baseball = SPORTS.find((s) => s.id === "baseball");

export default function BaseballPackage() {
  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="baseball-package">
      <SiteNav />
      <section className="relative pt-28 pb-16 border-b border-white/8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video src={baseball?.hero_video || "/hero-baseball.mp4"} autoPlay muted loop playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050810]/60 via-[#050810]/40 to-[#050810]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">Sport Intelligence Package</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
            BioHacker Baseball™<br/>
            <span className="text-white/50">Powered by ArmIQ™</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/75">
            The most complete baseball intelligence stack ever assembled. Every Intelligence Engine™
            wired into pitching mechanics, hitting biomechanics, defensive first-step reads, workload,
            recovery, and long-horizon development — all inside a single continuously-evolving
            {" "}Athlete Intelligence Profile™.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {baseball?.engines.map((eid) => {
              const eng = ENGINES.find((e) => e.id === eid);
              if (!eng) return null;
              return (
                <span key={eid} className="text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border"
                  style={{ borderColor: `${eng.tone}55`, color: eng.tone }}
                  data-testid={`baseball-engine-${eid}`}>
                  {eng.name}
                </span>
              );
            })}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/request-demo" data-testid="baseball-cta-demo">
              <Button className="text-white font-bold text-sm px-6 py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
                Request demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/why" data-testid="baseball-cta-why">
              <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
                Why BioHacker Baseball™
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 max-w-6xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">Built for every role</div>
        <h2 className="font-display font-black text-3xl text-white tracking-tight">One package. Every seat in the dugout.</h2>
        <div className="mt-8 space-y-4">
          {AUDIENCES.map((a) => (
            <div key={a.id} className="ink-card rounded-2xl p-6 grid md:grid-cols-4 gap-6" data-testid={`baseball-audience-${a.id}`}>
              <div className="font-display font-black text-white text-lg">{a.label}</div>
              <div className="md:col-span-3 text-sm text-white/75 leading-relaxed">{a.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Stride length — a peer-reviewed measurement MotionIQ scores automatically */}
      <StrideLengthCallout />

      <section className="py-14 border-t border-white/8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Cpu className="h-8 w-8 mx-auto text-fuchsia-400 mb-3" />
          <h2 className="font-display font-black text-3xl text-white tracking-tight">The rest of the sports catalog</h2>
          <p className="mt-3 text-white/60">Every Sport Intelligence Package™ shares the same platform, cloud, and profile.</p>
          <div className="mt-6">
            <Link to="/sports">
              <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm">
                See all packages <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      <WorkloadRiskModule sportId="baseball" />
      <SiteFooter />
    </div>
  );
}

/**
 * StrideLengthCallout — peer-reviewed stride-length benchmarking card.
 *
 * Sources cited: Manzi et al. 2021 (Journal of Sports Sciences, 315 pro
 * pitchers) — every 10% more stride length adds ~2 mph of pitching
 * velocity with zero added elbow stress. Fleisig group, Sports Health
 * 2022 — clinical standard stride ≈ 85% of body height. Amateur cohort
 * commonly measures below 75% — MotionIQ pose capture surfaces the
 * exact number so a coach can prescribe correctly.
 *
 * Includes an inline calculator (height in inches → recommended stride
 * feet + inches) plus the honest caveat about front-leg bracing.
 */
function StrideLengthCallout() {
  const [heightIn, setHeightIn] = useState(72); // 6'0"
  const target = +(heightIn * 0.85).toFixed(1);
  const amateur = +(heightIn * 0.75).toFixed(1);
  const gapIn = +(target - amateur).toFixed(1);
  const gapMph = +((gapIn / heightIn) * 100 / 10 * 2).toFixed(1); // ~10% ≈ 2mph
  const fmt = (inches) => {
    const ft = Math.floor(inches / 12);
    const rem = +(inches - ft * 12).toFixed(1);
    return `${ft}' ${rem}"`;
  };

  return (
    <section className="py-16 border-t border-white/8 bg-black/40" data-testid="baseball-stride-length">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1.05fr,0.95fr] gap-10 items-start">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3 flex items-center gap-2">
            <Ruler className="h-3.5 w-3.5" /> MotionIQ™ · Pitcher stride length
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight leading-[1.1]">
            Free velocity nobody is coaching.<br />
            <span className="text-white/50">Because nobody is measuring it.</span>
          </h2>
          <p className="mt-5 text-white/80 leading-relaxed">
            <b className="text-white">315 professional pitchers</b> were measured in a peer-reviewed study
            (<a href="https://pubmed.ncbi.nlm.nih.gov/33769195/" target="_blank" rel="noreferrer" className="underline hover:text-fuchsia-300">Manzi et al. 2021, Journal of Sports Sciences</a>):
            every <b className="text-white">10% more stride length</b> added roughly <b className="text-white">2 mph</b> of pitching velocity —
            with <b className="text-white">zero added stress on the elbow</b>. The clinical standard is a stride of
            <b className="text-white"> ~85% of your height</b> (Fleisig&apos;s group, <i>Sports Health</i> 2022). Most
            amateur pitchers we scan sit <b className="text-white">under 75%</b>. That gap is free velocity that
            nobody is coaching because nobody is measuring it.
          </p>
          <div className="mt-5 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-white/85"
               data-testid="stride-length-caveat">
            <b className="text-amber-300">Honest caveat:</b> a longer stride only adds velo if the
            <b className="text-white"> front leg can brace it</b>. Force-plate + MotionIQ block leg data first —
            then train the stride. Measurement before prescription.
          </div>
        </div>

        {/* Interactive calculator */}
        <div className="ink-card rounded-2xl p-6" data-testid="stride-length-calculator">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 font-bold mb-3">Your target stride</div>
          <label className="block text-xs text-white/60 mb-2" htmlFor="stride-height">Athlete height (inches)</label>
          <div className="flex items-center gap-3">
            <input
              id="stride-height"
              type="range"
              min={60}
              max={84}
              step={1}
              value={heightIn}
              onChange={(e) => setHeightIn(parseInt(e.target.value, 10) || 72)}
              className="flex-1 accent-red-500"
              data-testid="stride-height-slider"
            />
            <div className="font-display font-black text-2xl text-white tabular-nums w-24 text-right"
                 data-testid="stride-height-value">
              {fmt(heightIn)}
            </div>
          </div>

          <div className="mt-5 space-y-2 text-sm">
            <div className="flex items-center justify-between rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-bold">Target · 85%</div>
                <div className="text-white/60 text-xs">Fleisig 2022 clinical standard</div>
              </div>
              <div className="font-display font-black text-xl text-white tabular-nums" data-testid="stride-target">{fmt(target)}</div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2.5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-red-300 font-bold">Amateur avg · &lt;75%</div>
                <div className="text-white/60 text-xs">Where most scans land today</div>
              </div>
              <div className="font-display font-black text-xl text-white tabular-nums" data-testid="stride-amateur">{fmt(amateur)}</div>
            </div>
            <div className="mt-3 flex items-center justify-between rounded-md border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-2.5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-300 font-bold">Coach-able gap</div>
                <div className="text-white/60 text-xs">Manzi 2021 velocity link · elbow-neutral</div>
              </div>
              <div className="text-right">
                <div className="font-display font-black text-xl text-white tabular-nums" data-testid="stride-gap">+{gapIn}&quot;</div>
                <div className="text-[11px] text-emerald-300 tabular-nums" data-testid="stride-gap-mph">≈ +{gapMph} mph</div>
              </div>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-white/8 text-[11px] text-white/50">
            MotionIQ™ pose capture computes stride length automatically from a single side-view video —
            no markers, no radar, no manual tape measure required.
          </div>
        </div>
      </div>
    </section>
  );
}
